import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import { PageLoader } from 'src/app/components/lib/loader';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import ActionsMenu from 'src/app/layout/main/actions/actions-menu';
import { useBeeFreeSetup } from 'src/app/pages/marketing/campaigns/beefree-embed/use-beefree-setup';
import { emptyTemplate } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/shared/empty-template';
import { useGetCampaignTemplateQuery } from 'src/app/queries/marketing/campaign-templates/get-campaign-template';
import { useSaveCampaignTemplateMutation } from 'src/app/queries/marketing/campaign-templates/upsert-campaign-template';
import { useNotificationActions } from 'src/app/state/notifications';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';
import { usePermissionCheck } from 'src/app/state/user-permissions';

import { SlideContainer } from '../../../workflows/workflow-campaign-builder/shared/styles';
import { useTemplateBuilderContext } from '../wizard/builder-context';
import { getCampaignTemplatesPayload } from '../wizard/helpers';

import type BeefreeSDK from '@beefree.io/sdk';
import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';
import type { Action } from 'src/app/layout/main/actions/actions-menu';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

export function CampaignTemplateBeeFreeEditor({
  enableNextButton,
  disableNextButton,
  setComplete,
  setIncomplete,
  disableSaveAndExit,
  enableSaveAndExit,
  currentStep,
}: WizardStepComponentProps) {
  const beeFreeContainerRef = useRef(null);
  const beeFreePlugin = useRef<BeefreeSDK | null>(null);
  const notifications = useNotificationActions();
  const [mergeTagPreviewActive, setMergeTagPreviewActive] = useState(false);
  const { locProfile } = useRecoilValue(userDispensariesAtom);

  // perms
  const canCreateEditTemplates = usePermissionCheck([Permissions.CreateEditEmailTemplates]);

  // state
  const [builderState, dispatch] = useTemplateBuilderContext();
  const [beefreeEditing, setBeefreeEditing] = useState(false);
  const { marketingTemplatePresetId } = builderState;

  const params = useParams()?.['*'];
  const isEdit = useMemo(() => params?.includes('edit'), [params]);
  const templateIdFromURL = !isEdit ? null : params?.split('/').pop();

  const isUpdateFixEnabled = useLDFlag(LD.TEMPLATE_UPDATE_FIX_ROLLOUT, false);

  // Validate templateIdFromURL to ensure it's a valid number
  const parsedTemplateId = useMemo(() => {
    if (!templateIdFromURL) {
      return null;
    }
    const parsed = parseInt(templateIdFromURL, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [templateIdFromURL]);

  const {
    data: savedTemplate,
    isLoading: savedTemplateIsLoading,
    isError: getSavedTemplateIsError,
  } = useGetCampaignTemplateQuery({
    marketingTemplatePresetId: parsedTemplateId ?? 0,
    enabled: isEdit && !!parsedTemplateId,
  });

  const { mutateAsync: saveCampaignTemplate } = useSaveCampaignTemplateMutation();

  // bee-free plugin and handlers
  const [beefreeIsLoading, setBeeFreeIsLoading] = useState(false);
  const beeFreeEnabled = true;
  const waitingForBeefree = beefreeIsLoading && beeFreeEnabled;

  const parsedSavedEmailJSON = useMemo(
    () => (savedTemplate?.JsonBody ? JSON.parse(savedTemplate.JsonBody) : null),
    [savedTemplate?.JsonBody]
  );

  const templateForInit = parsedSavedEmailJSON ?? savedTemplate ?? emptyTemplate;

  const handleBFReady = useCallback(() => {
    setBeeFreeIsLoading(false);
    beeFreePlugin.current?.save();
  }, []);

  const { marketingTemplatePresetId: builderStateMarketingTemplatePresetId } = builderState;
  const BEEFREE_STEP_TITLES = ['Editor'];
  const isCorrectStepForCurrentStep = BEEFREE_STEP_TITLES.includes(currentStep);

  useEffect(() => {
    // this would normally render before we land on this step
    // so controlling this to only render when we're on the editor step avoid disabling the save and exit button uncessarily
    if (!isCorrectStepForCurrentStep) {
      return;
    }
    if (
      marketingTemplatePresetId &&
      !!builderStateMarketingTemplatePresetId &&
      !savedTemplateIsLoading &&
      !getSavedTemplateIsError &&
      canCreateEditTemplates &&
      !beefreeIsLoading &&
      !beefreeEditing
    ) {
      setComplete();
      enableNextButton();
      enableSaveAndExit();
    } else {
      setIncomplete();
      disableNextButton();
      disableSaveAndExit();
    }
  }, [
    isCorrectStepForCurrentStep,
    builderStateMarketingTemplatePresetId,
    savedTemplateIsLoading,
    getSavedTemplateIsError,
    canCreateEditTemplates,
    beefreeIsLoading,
    beefreeEditing,
    setComplete,
    setIncomplete,
    enableNextButton,
    disableNextButton,
    enableSaveAndExit,
    disableSaveAndExit,
    marketingTemplatePresetId,
  ]);

  const handleSave = useCallback(
    async (templateJSON: string, templateHTML: string) => {
      try {
        await saveCampaignTemplate(
          getCampaignTemplatesPayload(
            {
              JsonBody: templateJSON,
              HtmlBody: templateHTML,
              MarketingTemplatePresetId: builderStateMarketingTemplatePresetId ?? 0,
            },
            builderState
          ),
          {
            onSuccess: () => {
              setBeefreeEditing(false);
            },
            onError: (e) => {
              const error = e as ApiErrorResponse;
              const { data, status } = error.response;
              // a 400 error will return a user-friendly error message
              if (status === 400) {
                notifications.error({ message: data.Message });
              } else {
                notifications.error({ message: 'Error saving email template' });
              }
            },
          }
        );
      } catch (error) {
        console.error(error);
        notifications.error({ message: 'Error saving email template' });
        setBeefreeEditing(false);
      }
    },
    [builderState, saveCampaignTemplate, notifications, builderStateMarketingTemplatePresetId]
  );

  const handleSaveV2 = useCallback(
    async (templateJSON: string, templateHTML: string) => {
      try {
        const payload = getCampaignTemplatesPayload(
          {
            JsonBody: templateJSON,
            HtmlBody: templateHTML,
            MarketingTemplatePresetId: builderStateMarketingTemplatePresetId ?? 0,
          },
          builderState
        );

        await saveCampaignTemplate(payload, {
          onSuccess: (data) => {
            dispatch({
              type: 'set-value',
              payload: { key: 'lastModifiedDateUtc', value: data.LastModifiedDateUtc },
            });
            setBeefreeEditing(false);
          },
          onError: (e) => {
            const error = e as ApiErrorResponse;
            const { data, status } = error.response;
            if (status === 409) {
              notifications.error({ message: data.Message });
            } else {
              notifications.error({ message: 'Error saving email template' });
            }
            setBeefreeEditing(false);
          },
        });
      } catch (error) {
        setBeefreeEditing(false);
      }
    },
    [builderState, saveCampaignTemplate, notifications, builderStateMarketingTemplatePresetId, dispatch]
  );

  const handleChange = useCallback(() => {
    setBeefreeEditing(true);
    beeFreePlugin.current?.save();
  }, [beeFreePlugin]);

  // Debounce timer ref for auto-save after typing stops
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChangeV2 = useCallback(() => {
    setBeefreeEditing(true);

    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set a new timeout to save after user stops typing for 2 seconds
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (beeFreePlugin.current?.save) {
        beeFreePlugin.current.save();
      }
    }, 2000);
  }, []);

  const saveCallbackRef = useRef(handleSave);
  const saveCallbackRefV2 = useRef(handleSaveV2);

  useEffect(() => {
    saveCallbackRef.current = handleSave;
    saveCallbackRefV2.current = handleSaveV2;
  }, [handleSave, handleSaveV2]);

  const { beeFreeInstance } = useBeeFreeSetup({
    waitForTemplateLoad: !!builderStateMarketingTemplatePresetId && savedTemplateIsLoading,
    beeFreeContainerRef,
    templateJSON: templateForInit,
    onSave: (...args) => (isUpdateFixEnabled ? saveCallbackRefV2.current(...args) : saveCallbackRef.current(...args)),
    onReady: handleBFReady,
    onChange: isUpdateFixEnabled ? handleChangeV2 : handleChange,
    isEnabled: beeFreeEnabled,
    containerId: 'bee-plugin-container-templates',
    enableAbandonedCartAddOn: false, // Templates are not tied to specific event types
  });

  useEffect(() => {
    beeFreePlugin.current = beeFreeInstance;
  }, [beeFreeInstance]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    },
    []
  );

  if (getSavedTemplateIsError) {
    notifications.error({
      message: 'Could not load saved template. You can still create new content or try again later.',
    });
  }

  const emailIsActive = locProfile.UseEmailMarketing ?? false;
  const smsIsActive = locProfile.UseSMSMarketing ?? false;

  const actions: Action[] = [];

  if (emailIsActive || smsIsActive) {
    actions.push({
      label: 'Preview',
      onClick: () => beeFreePlugin.current?.preview(),
      visible: true,
    });

    actions.push({
      label: `${mergeTagPreviewActive ? 'Hide' : 'Show'} merge tags Preview`,
      onClick: () => {
        beeFreePlugin.current?.toggleMergeTagsPreview();
        setMergeTagPreviewActive((prev) => !prev);
      },
      visible: true,
    });
  }

  return (
    <StyledSlideContainer>
      {waitingForBeefree && (
        <LoaderContainer>
          <PageLoader />
        </LoaderContainer>
      )}
      <EditorContainer $isLoading={waitingForBeefree}>
        <EditorHeader>
          <StyledActionsMenu actions={actions} />
        </EditorHeader>
        {beeFreeEnabled && <BFContainer id='bee-plugin-container-templates' ref={beeFreeContainerRef} />}
      </EditorContainer>
    </StyledSlideContainer>
  );
}

const StyledSlideContainer = styled(SlideContainer)`
  overflow-y: hidden;
`;
const EditorContainer = styled.div<{ $isLoading: boolean }>`
  display: ${({ $isLoading }) => ($isLoading ? 'none' : 'block')};
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;
const EditorHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: var(--sizes-50) var(--sizes-60);
  background-color: white;
  border-bottom: 1px solid var(--color-gray-20);
  top: 0px;
`;
const StyledActionsMenu = styled(ActionsMenu)`
  background-color: var(--color-brand-primary-white);
  border: 1px solid var(--color-gray-70);
`;
const BFContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: hidden;
`;
const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
